/**
 * AddElementsPanel - Wix-style 3-pane Add Elements Library
 * Location: /app/frontend/src/admin/page_builder/components/AddElementsPanel.js
 * 
 * Features:
 * - Left pane: Category list (generated from template registry)
 * - Middle pane: Sub-categories and filters (Recommended, Most Used, New, Data-backed, Static)
 * - Right pane: Template tiles grid with thumbnails
 * - Search bar at top (filters by name/tags)
 * - Clicking a tile inserts a section via existing add-section flow
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  getCategoriesWithTemplates,
  searchTemplates,
  getTemplatesWithMetadata,
  getTemplatesByCategory,
  getSubCategoriesForCategory,
  getFilteredTemplates,
  CategoryMeta,
  FilterType,
  FilterMeta,
  getTemplateMetadata,
} from '../templates';
import {
  Search,
  X,
  Layout,
  Grid3x3,
  BookOpen,
  User,
  Megaphone,
  Image as ImageIcon,
  Settings,
  Sparkles,
  TrendingUp,
  PlusCircle,
  Database,
  FileText,
  ChevronRight,
  Plus,
  Zap,
  Library,
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Badge } from '../../../components/ui/badge';

// Icon mapping for categories
const CATEGORY_ICONS = {
  hero: Layout,
  content: Grid3x3,
  reader: BookOpen,
  creator: User,
  cta: Megaphone,
  series: Library,
  gallery: ImageIcon,
  admin: Settings,
};

// Icon mapping for filters
const FILTER_ICONS = {
  [FilterType.RECOMMENDED]: Sparkles,
  [FilterType.MOST_USED]: TrendingUp,
  [FilterType.NEW]: PlusCircle,
  [FilterType.DATA_BACKED]: Database,
  [FilterType.STATIC]: FileText,
};

/**
 * TemplateThumbnail - Generates a visual thumbnail for templates
 */
const TemplateThumbnail = ({ template, categoryMeta }) => {
  const isDataBacked = template.isDataBacked;
  
  // Generate SVG-based visual representation based on template type
  const renderThumbnailPattern = () => {
    const baseColor = categoryMeta?.color || '#f97316';
    
    // Different patterns for different template types
    if (template.category === 'hero') {
      return (
        <svg className="w-full h-full" viewBox="0 0 120 80">
          <rect x="0" y="0" width="120" height="80" fill={`${baseColor}10`} />
          <rect x="10" y="25" width="60" height="8" rx="2" fill={`${baseColor}40`} />
          <rect x="10" y="38" width="80" height="4" rx="1" fill={`${baseColor}20`} />
          <rect x="10" y="50" width="30" height="12" rx="2" fill={baseColor} />
          <rect x="45" y="50" width="30" height="12" rx="2" fill={`${baseColor}30`} stroke={baseColor} strokeWidth="1" />
        </svg>
      );
    }
    
    if (template.category === 'content' || template.category === 'gallery') {
      const isGrid = template.id.includes('grid');
      const cols = isGrid ? 3 : 4;
      return (
        <svg className="w-full h-full" viewBox="0 0 120 80">
          <rect x="0" y="0" width="120" height="80" fill={`${baseColor}05`} />
          <rect x="10" y="8" width="50" height="6" rx="1" fill={`${baseColor}30`} />
          {[...Array(cols)].map((_, i) => (
            <g key={i}>
              <rect 
                x={10 + i * (isGrid ? 35 : 26)} 
                y="20" 
                width={isGrid ? 30 : 22} 
                height={isGrid ? 40 : 50} 
                rx="2" 
                fill={`${baseColor}${20 + i * 5}`} 
              />
              <rect 
                x={12 + i * (isGrid ? 35 : 26)} 
                y={isGrid ? 64 : 72} 
                width={isGrid ? 20 : 16} 
                height="3" 
                rx="1" 
                fill={`${baseColor}40`} 
              />
            </g>
          ))}
        </svg>
      );
    }
    
    if (template.category === 'creator') {
      return (
        <svg className="w-full h-full" viewBox="0 0 120 80">
          <rect x="0" y="0" width="120" height="80" fill={`${baseColor}05`} />
          <circle cx="35" cy="35" r="15" fill={`${baseColor}30`} />
          <rect x="55" y="25" width="50" height="6" rx="1" fill={`${baseColor}40`} />
          <rect x="55" y="35" width="35" height="4" rx="1" fill={`${baseColor}20`} />
          <rect x="55" y="50" width="25" height="8" rx="2" fill={baseColor} />
        </svg>
      );
    }
    
    if (template.category === 'reader') {
      return (
        <svg className="w-full h-full" viewBox="0 0 120 80">
          <rect x="0" y="0" width="120" height="80" fill={`${baseColor}05`} />
          {[...Array(4)].map((_, i) => (
            <g key={i}>
              <rect x="10" y={10 + i * 17} width="100" height="14" rx="2" fill={`${baseColor}${15 + i * 5}`} />
              <rect x="15" y={13 + i * 17} width="8" height="8" rx="1" fill={`${baseColor}30`} />
              <rect x="28" y={14 + i * 17} width="40" height="3" rx="1" fill={`${baseColor}40`} />
              <rect x="28" y={19 + i * 17} width="25" height="2" rx="1" fill={`${baseColor}20`} />
            </g>
          ))}
        </svg>
      );
    }
    
    if (template.category === 'cta') {
      return (
        <svg className="w-full h-full" viewBox="0 0 120 80">
          <rect x="0" y="0" width="120" height="80" fill={baseColor} rx="4" />
          <rect x="20" y="25" width="80" height="8" rx="2" fill="white" fillOpacity="0.9" />
          <rect x="30" y="38" width="60" height="4" rx="1" fill="white" fillOpacity="0.5" />
          <rect x="35" y="50" width="50" height="12" rx="2" fill="white" />
        </svg>
      );
    }
    
    // Default pattern
    return (
      <svg className="w-full h-full" viewBox="0 0 120 80">
        <rect x="0" y="0" width="120" height="80" fill={`${baseColor}10`} />
        <rect x="10" y="10" width="100" height="60" rx="4" fill={`${baseColor}15`} stroke={`${baseColor}30`} strokeWidth="1" />
        <rect x="20" y="30" width="40" height="6" rx="1" fill={`${baseColor}40`} />
        <rect x="20" y="40" width="60" height="4" rx="1" fill={`${baseColor}20`} />
      </svg>
    );
  };
  
  return (
    <div className="relative w-full aspect-[3/2] bg-zinc-900 rounded-lg overflow-hidden">
      {renderThumbnailPattern()}
      {isDataBacked && (
        <div className="absolute top-1 right-1">
          <Database className="w-3 h-3 text-blue-400" />
        </div>
      )}
    </div>
  );
};

/**
 * TemplateCard - Individual template card in the grid
 */
const TemplateCard = ({ template, onSelect }) => {
  const categoryMeta = CategoryMeta[template.category];
  const meta = getTemplateMetadata(template.id);
  
  return (
    <button
      onClick={() => onSelect(template)}
      className="group text-left bg-zinc-800/30 border border-zinc-700/30 rounded-lg overflow-hidden
                 hover:border-orange-500/50 hover:bg-zinc-800/60 transition-all p-3"
      data-testid={`template-card-${template.id}`}
    >
      {/* Thumbnail */}
      <TemplateThumbnail template={{ ...template, ...meta }} categoryMeta={categoryMeta} />
      
      {/* Info */}
      <div className="mt-2">
        <div className="flex items-start justify-between gap-1">
          <h4 className="text-sm font-medium text-white truncate group-hover:text-orange-400 transition-colors">
            {template.name}
          </h4>
          {meta.isNew && (
            <Badge className="bg-green-500/20 text-green-400 text-[10px] px-1.5 py-0">NEW</Badge>
          )}
        </div>
        <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
          {template.description}
        </p>
        
        {/* Tags preview */}
        {meta.tags && meta.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {meta.tags.slice(0, 3).map(tag => (
              <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-zinc-700/50 text-zinc-400 rounded">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      
      {/* Add indicator */}
      <div className="absolute bottom-3 right-3 w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center 
                      opacity-0 group-hover:opacity-100 transition-opacity transform scale-75 group-hover:scale-100">
        <Plus className="w-4 h-4 text-white" />
      </div>
    </button>
  );
};

/**
 * CategoryItem - Left pane category item
 */
const CategoryItem = ({ category, meta, count, isSelected, onClick }) => {
  const IconComponent = CATEGORY_ICONS[category] || Layout;
  
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all
        ${isSelected 
          ? 'bg-zinc-800 text-white' 
          : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-300'
        }
      `}
      data-testid={`category-${category}`}
    >
      <div 
        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
          isSelected ? '' : 'bg-zinc-800/50'
        }`}
        style={{ 
          backgroundColor: isSelected ? `${meta.color}20` : undefined 
        }}
      >
        <IconComponent 
          className="w-4 h-4" 
          style={{ color: isSelected ? meta.color : undefined }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium block truncate">
          {meta.label.replace(' Sections', '').replace(' & Utility', '')}
        </span>
      </div>
      <span className={`text-xs ${isSelected ? 'text-zinc-400' : 'text-zinc-600'}`}>
        {count}
      </span>
    </button>
  );
};

/**
 * FilterItem - Middle pane filter item
 */
const FilterItem = ({ filterType, isSelected, onClick, count }) => {
  const meta = FilterMeta[filterType];
  const IconComponent = FILTER_ICONS[filterType] || Sparkles;
  
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all
        ${isSelected 
          ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
          : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-300 border border-transparent'
        }
      `}
      data-testid={`filter-${filterType}`}
    >
      <IconComponent className="w-4 h-4 flex-shrink-0" />
      <span className="text-sm flex-1">{meta.label}</span>
      {count !== undefined && (
        <span className="text-xs text-zinc-500">{count}</span>
      )}
    </button>
  );
};

/**
 * SubCategoryItem - Middle pane sub-category item
 */
const SubCategoryItem = ({ subCategory, isSelected, onClick, count }) => {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all
        ${isSelected 
          ? 'bg-zinc-700/50 text-white' 
          : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-300'
        }
      `}
      data-testid={`subcategory-${subCategory.id}`}
    >
      <span className="text-sm">{subCategory.label}</span>
      <span className="text-xs text-zinc-500">{count}</span>
    </button>
  );
};

/**
 * AddElementsPanel - Main 3-pane component
 */
const AddElementsPanel = ({ 
  isOpen, 
  onClose, 
  onSelectTemplate,
  pageType = null, // Current page type for recommendations
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  
  // Get all data from registry
  const categoriesWithTemplates = useMemo(() => getCategoriesWithTemplates(), []);
  const allTemplatesWithMeta = useMemo(() => getTemplatesWithMetadata(), []);
  
  // Get sub-categories for selected category
  const subCategories = useMemo(() => {
    if (!selectedCategory) return [];
    return getSubCategoriesForCategory(selectedCategory);
  }, [selectedCategory]);
  
  // Compute displayed templates based on selections
  const displayedTemplates = useMemo(() => {
    // If searching, filter by search
    if (searchQuery.trim()) {
      const results = searchTemplates(searchQuery);
      // Merge metadata into results
      return results.map(t => ({
        ...t,
        ...getTemplateMetadata(t.id),
      }));
    }
    
    let templates = allTemplatesWithMeta;
    
    // Filter by category
    if (selectedCategory) {
      templates = templates.filter(t => t.category === selectedCategory);
    }
    
    // Filter by sub-category
    if (selectedSubCategory) {
      templates = templates.filter(t => t.subCategory === selectedSubCategory);
    }
    
    // Filter by filter type
    if (selectedFilter) {
      switch (selectedFilter) {
        case FilterType.RECOMMENDED:
          templates = [...templates].sort((a, b) => b.popularity - a.popularity).slice(0, Math.ceil(templates.length * 0.6));
          break;
        case FilterType.MOST_USED:
          templates = [...templates].sort((a, b) => b.popularity - a.popularity);
          break;
        case FilterType.NEW:
          templates = templates.filter(t => t.isNew);
          break;
        case FilterType.DATA_BACKED:
          templates = templates.filter(t => t.isDataBacked);
          break;
        case FilterType.STATIC:
          templates = templates.filter(t => !t.isDataBacked);
          break;
      }
    }
    
    return templates;
  }, [searchQuery, selectedCategory, selectedSubCategory, selectedFilter, allTemplatesWithMeta]);
  
  // Compute filter counts
  const filterCounts = useMemo(() => {
    let baseTemplates = allTemplatesWithMeta;
    if (selectedCategory) {
      baseTemplates = baseTemplates.filter(t => t.category === selectedCategory);
    }
    
    return {
      [FilterType.RECOMMENDED]: Math.ceil(baseTemplates.length * 0.6),
      [FilterType.MOST_USED]: baseTemplates.length,
      [FilterType.NEW]: baseTemplates.filter(t => t.isNew).length,
      [FilterType.DATA_BACKED]: baseTemplates.filter(t => t.isDataBacked).length,
      [FilterType.STATIC]: baseTemplates.filter(t => !t.isDataBacked).length,
    };
  }, [selectedCategory, allTemplatesWithMeta]);
  
  // Compute sub-category counts
  const subCategoryCounts = useMemo(() => {
    if (!selectedCategory) return {};
    const categoryTemplates = allTemplatesWithMeta.filter(t => t.category === selectedCategory);
    const counts = {};
    subCategories.forEach(sc => {
      counts[sc.id] = categoryTemplates.filter(t => t.subCategory === sc.id).length;
    });
    return counts;
  }, [selectedCategory, subCategories, allTemplatesWithMeta]);
  
  // Handle template selection
  const handleSelect = useCallback((template) => {
    onSelectTemplate(template);
    onClose();
  }, [onSelectTemplate, onClose]);
  
  // Reset filters when category changes
  useEffect(() => {
    setSelectedSubCategory(null);
  }, [selectedCategory]);
  
  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center"
      onClick={onClose}
      data-testid="add-elements-panel"
    >
      <div 
        className="bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl 
                   w-[95vw] max-w-6xl h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <Plus className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Add Elements</h2>
              <p className="text-sm text-zinc-500">
                {searchQuery 
                  ? `${displayedTemplates.length} results for "${searchQuery}"`
                  : selectedCategory 
                    ? `${displayedTemplates.length} templates in ${CategoryMeta[selectedCategory]?.label}`
                    : `${allTemplatesWithMeta.length} templates available`
                }
              </p>
            </div>
          </div>
          
          {/* Search */}
          <div className="flex-1 max-w-md mx-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search templates by name, tag, or category..."
                className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="template-search"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400 hover:text-white transition-colors"
            data-testid="close-panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* 3-Pane Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Pane - Categories */}
          <div className="w-56 border-r border-zinc-800 flex flex-col">
            <div className="p-3 border-b border-zinc-800/50">
              <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">
                Categories
              </span>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {/* All Templates */}
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setSelectedSubCategory(null);
                    setSelectedFilter(null);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all
                    ${!selectedCategory 
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
                      : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-300 border border-transparent'
                    }
                  `}
                  data-testid="category-all"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    !selectedCategory ? 'bg-orange-500/20' : 'bg-zinc-800/50'
                  }`}>
                    <Grid3x3 className="w-4 h-4" style={{ color: !selectedCategory ? '#f97316' : undefined }} />
                  </div>
                  <span className="text-sm font-medium flex-1">All Templates</span>
                  <span className="text-xs text-zinc-500">{allTemplatesWithMeta.length}</span>
                </button>
                
                <div className="h-px bg-zinc-800 my-2" />
                
                {/* Category list */}
                {categoriesWithTemplates
                  .filter(({ meta }) => !meta.adminOnly) // Hide admin-only from regular view
                  .map(({ category, meta, templates }) => (
                    <CategoryItem
                      key={category}
                      category={category}
                      meta={meta}
                      count={templates.length}
                      isSelected={selectedCategory === category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setSelectedFilter(null);
                      }}
                    />
                  ))
                }
              </div>
            </ScrollArea>
          </div>
          
          {/* Middle Pane - Filters & Sub-categories */}
          <div className="w-48 border-r border-zinc-800 flex flex-col bg-zinc-900/30">
            <div className="p-3 border-b border-zinc-800/50">
              <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">
                Filters
              </span>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {/* Filter options */}
                {Object.values(FilterType).map(filterType => (
                  <FilterItem
                    key={filterType}
                    filterType={filterType}
                    isSelected={selectedFilter === filterType}
                    onClick={() => setSelectedFilter(selectedFilter === filterType ? null : filterType)}
                    count={filterCounts[filterType]}
                  />
                ))}
                
                {/* Sub-categories (if category selected) */}
                {selectedCategory && subCategories.length > 0 && (
                  <>
                    <div className="h-px bg-zinc-800 my-3" />
                    <div className="px-1 mb-2">
                      <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">
                        Sub-categories
                      </span>
                    </div>
                    {subCategories.map(sc => (
                      <SubCategoryItem
                        key={sc.id}
                        subCategory={sc}
                        isSelected={selectedSubCategory === sc.id}
                        onClick={() => setSelectedSubCategory(selectedSubCategory === sc.id ? null : sc.id)}
                        count={subCategoryCounts[sc.id] || 0}
                      />
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
          
          {/* Right Pane - Template Grid */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-3 border-b border-zinc-800/50 flex items-center justify-between">
              <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">
                {searchQuery 
                  ? 'Search Results'
                  : selectedCategory 
                    ? CategoryMeta[selectedCategory]?.label 
                    : 'All Templates'
                }
              </span>
              <span className="text-xs text-zinc-600">
                {displayedTemplates.length} template{displayedTemplates.length !== 1 ? 's' : ''}
              </span>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-4">
                {displayedTemplates.length > 0 ? (
                  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {displayedTemplates.map(template => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 rounded-full bg-zinc-800/50 flex items-center justify-center mb-4">
                      <Search className="w-8 h-8 text-zinc-600" />
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">No templates found</h3>
                    <p className="text-sm text-zinc-500 max-w-sm">
                      {searchQuery 
                        ? `No templates match "${searchQuery}". Try a different search term.`
                        : 'No templates match the current filters. Try adjusting your selection.'
                      }
                    </p>
                    {(searchQuery || selectedFilter || selectedSubCategory) && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedFilter(null);
                          setSelectedSubCategory(null);
                        }}
                        className="mt-4 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-lg transition-colors"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-3 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Database className="w-3 h-3 text-blue-400" />
              Data-backed sections fetch live content
            </span>
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-orange-400" />
              Click any template to add it to your page
            </span>
          </div>
          <span>
            Press <kbd className="px-1.5 py-0.5 bg-zinc-800 rounded text-zinc-400">ESC</kbd> to close
          </span>
        </div>
      </div>
    </div>
  );
};

export default AddElementsPanel;
